import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Laptop } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a virtual assistant invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A virtual assistant invoice should include your name and contact details, the client name and business name, a unique invoice number and date, payment due date, a description of the services provided (admin tasks, scheduling, research, etc.), hours worked or a monthly retainer fee, any tool or software costs passed on to the client, applicable tax if registered, and your payment details.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should virtual assistants charge hourly or a monthly retainer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Both models work for VAs. Hourly billing suits project-based or variable workloads where the scope changes week to week. Monthly retainers suit clients who need a consistent number of hours each month — they provide predictable income for you and predictable costs for the client. Many experienced VAs transition clients to retainers once the relationship is established.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do virtual assistants handle unused retainer hours on an invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'This depends on your retainer terms. If hours do not roll over, invoice the full retainer amount regardless of usage and note it in the invoice or accompanying report. If you offer rollover, track hours across months and reflect the balance clearly. Whichever approach you use, define it in your contract so clients understand the terms from day one.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can virtual assistants invoice clients in other countries?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Virtual assistants often work with international clients. Invoice in the agreed currency — usually the client\'s currency (USD, GBP, EUR, AUD). Use Invilic\'s international invoice option which supports 20+ currencies. You do not typically charge the client\'s country VAT/GST — you charge based on your own country\'s tax rules and registration status.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this virtual assistant invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free virtual assistant invoice template. Fill in your service details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function VirtualAssistantInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Virtual Assistant Invoice Template: Free PDF for VAs — Philippines, UK & US | Invilic"
        description="Free virtual assistant invoice template for Filipino, UK, and US VAs. Bill hourly, retainer, or per-project. USD, PHP, GBP supported. Instant PDF. No sign-up needed."
        canonical="https://www.invilic.com/virtual-assistant-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Virtual Assistant Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for virtual assistants. Bill for hourly work, monthly retainers, and admin services. Download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-cyan-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Virtual Assistant
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'General admin VAs', desc: 'Bill for inbox management, scheduling, data entry, and day-to-day admin support.' },
              { title: 'Social media VAs', desc: 'Invoice for content scheduling, community management, and social media reporting.' },
              { title: 'Executive & specialist VAs', desc: 'Bill for high-level executive support, project coordination, and research tasks.' },
              { title: 'E-commerce & Shopify VAs', desc: 'Issue invoices for product listing, order management, and customer service tasks.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-cyan-100 dark:bg-cyan-900/40 flex items-center justify-center flex-shrink-0">
                  <Laptop className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the VA Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Your name and contact details',
              'Client name and business name',
              'Invoice number and date',
              'Payment due date',
              'Services provided per line item',
              'Hours worked or monthly retainer fee',
              'Tool or software costs (if passed on)',
              'Hours summary or time log reference',
              'Subtotal, tax and discount',
              'Total amount due',
              'Bank details or payment link',
              'Notes and billing period',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-cyan-100 dark:border-cyan-800/30 bg-cyan-50 dark:bg-cyan-900/10">
                <CheckCircle className="w-4 h-4 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your VA Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Add your services', desc: 'Enter client details, services provided, hours or retainer fee, and billing period.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to send to your client.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-cyan-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Virtual Assistant Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* Philippines VA section */}
        <section className="mb-16 p-6 rounded-2xl border border-cyan-100 dark:border-cyan-800/40 bg-cyan-50 dark:bg-cyan-900/20">
          <h2 className="font-bold text-gray-900 dark:text-white mb-3">Filipino VA Invoicing — BIR, Payoneer & USD Billing</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            The Philippines has one of the world's largest virtual assistant communities. Key invoicing facts for Filipino VAs:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { label: 'Invoice in USD by default', detail: 'Most US, UK, and Australian clients pay in USD. Invoice in USD to avoid conversion friction and protect against PHP depreciation.' },
              { label: 'BIR TIN on invoices', detail: 'Include your BIR Tax Identification Number (TIN) on all invoices. Register your TIN free at BIR.gov.ph.' },
              { label: '12% VAT threshold: PHP 3 million', detail: 'VAT registration is required above PHP 3 million annual gross receipts. Services to foreign clients are zero-rated regardless.' },
              { label: 'Receive payment via Payoneer', detail: 'Payoneer is the most widely used platform for Filipino VAs. Withdraw to Philippine bank (BDO, BPI) or GCash.' },
            ].map(({ label, detail }) => (
              <div key={label} className="p-3 bg-white dark:bg-gray-900/50 rounded-xl border border-cyan-100 dark:border-cyan-800/30">
                <p className="font-semibold text-gray-900 dark:text-white text-xs mb-1">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your VA Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-cyan-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Virtual Assistant Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
